import React, { useEffect, useState } from 'react';
import { Box, Button, Stack, TextField, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { listBusinessTypes, BusinessType } from '../services/businessTypeService';
import axiosInstance from '../utils/axiosConfig';
import { useTranslation } from 'react-i18next';

const BusinessTypesPage: React.FC = () => {
  const { t } = useTranslation();
  const [businessTypes, setBusinessTypes] = useState<BusinessType[]>([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState('');
  const [error, setError] = useState<string | null>(null);

  const fetchBusinessTypes = async () => {
    setLoading(true);
    try {
      const data = await listBusinessTypes();
      setBusinessTypes(data);
    } catch (e) {
      setError('Failed to load business types');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBusinessTypes();
  }, []);

  const handleAdd = async () => {
    if (!newName.trim()) return;
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.post('/api/v1/business-types', { name: newName });
      setNewName('');
      fetchBusinessTypes();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to add business type');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    setLoading(true);
    setError(null);
    try {
      await axiosInstance.delete(`/api/v1/business-types/${id}`);
      fetchBusinessTypes();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Failed to delete business type');
    } finally {
      setLoading(false);
    }
  };

  const columns: GridColDef[] = [
    { field: 'id', headerName: 'ID', width: 80 },
    { field: 'name', headerName: t('businessTypes.name'), flex: 1 },
    {
      field: 'actions',
      headerName: t('businessTypes.actions'),
      width: 100,
      sortable: false,
      renderCell: (params) => (
        <IconButton color="error" onClick={() => handleDelete(params.row.id)}>
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  return (
    <Box p={2}>
      <h2>{t('businessTypes.title')}</h2>
      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <TextField
          label="New Business Type"
          value={newName}
          onChange={e => setNewName(e.target.value)}
          size="small"
        />
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAdd}
          disabled={loading || !newName.trim()}
        >
          {t('businessTypes.add')}
        </Button>
      </Stack>
      {error && <Box color="error.main" mb={2}>{error}</Box>}
      <DataGrid
        rows={businessTypes}
        columns={columns}
        loading={loading}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50]}
      />
    </Box>
  );
};

export default BusinessTypesPage; 